import React, { useEffect, useState } from 'react';

/**
 * VerifyConsent page
 *
 * This is the page parents land on when they click the consent link in their email.
 * It calls the verify-consent Edge Function with the token, then shows the result.
 *
 * URL: /verify-consent?token=xxx
 * URL: /verify-consent?token=xxx&action=revoke
 */

const EDGE_FN_URL = 'https://kvscdxgfofbcewkysbvi.supabase.co/functions/v1';

const VerifyConsent: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'verified' | 'already_verified' | 'revoked' | 'invalid' | 'error'>('loading');
  const [childName, setChildName] = useState('your child');

  useEffect(() => {
    verifyConsent();
  }, []);

  const verifyConsent = async () => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const action = params.get('action');

    if (!token) {
      setStatus('invalid');
      return;
    }

    try {
      const url = `${EDGE_FN_URL}/verify-consent?token=${encodeURIComponent(token)}${action ? `&action=${action}` : ''}`;
      const res = await fetch(url, { method: 'POST' });
      const data = await res.json();

      if (data.success) {
        if (action === 'revoke') {
          setStatus('revoked');
        } else if (data.message === 'Already verified') {
          setStatus('already_verified');
        } else {
          setStatus('verified');
        }
        // Extract child name from redirect URL if present
        if (data.redirect) {
          const redirectParams = new URLSearchParams(data.redirect.split('?')[1] || '');
          const name = redirectParams.get('name');
          if (name) setChildName(decodeURIComponent(name));
        }
      } else {
        setStatus('invalid');
      }
    } catch (err) {
      console.error('Verification error:', err);
      setStatus('error');
    }
  };

  const getContent = () => {
    switch (status) {
      case 'loading':
        return {
          emoji: '⏳',
          title: 'Verifying...',
          message: 'Please wait while we confirm your consent.',
          subMessage: '',
          color: '#4FCCC6',
        };
      case 'verified':
        return {
          emoji: '🎉',
          title: 'Consent Confirmed!',
          message: `${childName} can now use ElevenUp. They're all set to start their 11+ preparation!`,
          subMessage: 'You can close this page — the app will update automatically.',
          color: '#4FCCC6',
        };
      case 'already_verified':
        return {
          emoji: '✅',
          title: 'Already Verified',
          message: 'Your consent was already confirmed. No further action needed!',
          subMessage: 'You can close this page.',
          color: '#4FCCC6',
        };
      case 'revoked':
        return {
          emoji: '🔒',
          title: 'Consent Revoked',
          message: `Your consent has been withdrawn. Access to ElevenUp has been paused.`,
          subMessage: 'If you change your mind, you can create a new account or contact us.',
          color: '#F56565',
        };
      case 'invalid':
        return {
          emoji: '❌',
          title: 'Invalid Link',
          message: 'This consent link is invalid or has expired.',
          subMessage: 'Please check your email for a more recent link, or sign in and request a new one.',
          color: '#F56565',
        };
      case 'error':
      default:
        return {
          emoji: '❓',
          title: 'Something went wrong',
          message: 'We couldn\'t process your verification. Please try again.',
          subMessage: 'If the problem persists, contact us for help.',
          color: '#F56565',
        };
    }
  };

  const content = getContent();

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.emoji}>{content.emoji}</div>
        <h1 style={{ ...styles.title, color: content.color }}>{content.title}</h1>
        <p style={styles.message}>{content.message}</p>
        {content.subMessage && <p style={styles.subMessage}>{content.subMessage}</p>}

        {(status === 'verified' || status === 'already_verified') && (
          <div style={styles.infoBox}>
            <p style={styles.infoTitle}>Your rights as a parent</p>
            <ul style={styles.infoList}>
              <li>You can request a copy of your child's data at any time</li>
              <li>You can ask us to delete all data at any time</li>
              <li>You can withdraw consent by contacting us</li>
            </ul>
          </div>
        )}

        <div style={styles.links}>
          <a href="/privacy" style={styles.link}>Privacy Policy</a>
          <span style={styles.dot}>·</span>
          <a href="/terms" style={styles.link}>Terms of Service</a>
          <span style={styles.dot}>·</span>
          <a href="mailto:rain@elevenup.xyz" style={styles.link}>Contact Us</a>
        </div>

        <p style={styles.footer}>© {new Date().getFullYear()} 11 Up Company Ltd</p>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#0f0f23',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  card: {
    backgroundColor: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 20,
    padding: '48px 36px',
    maxWidth: 480,
    width: '100%',
    textAlign: 'center' as const,
  },
  emoji: {
    fontSize: 64,
    marginBottom: 20,
  },
  title: {
    fontSize: 28,
    fontWeight: 800,
    margin: '0 0 16px',
  },
  message: {
    fontSize: 16,
    lineHeight: 1.6,
    color: 'rgba(255,255,255,0.8)',
    margin: '0 0 12px',
  },
  subMessage: {
    fontSize: 14,
    lineHeight: 1.5,
    color: 'rgba(255,255,255,0.5)',
    margin: '0 0 32px',
  },
  infoBox: {
    backgroundColor: 'rgba(79, 204, 198, 0.06)',
    border: '1px solid rgba(79, 204, 198, 0.15)',
    borderRadius: 12,
    padding: '16px 20px',
    marginBottom: 32,
    textAlign: 'left' as const,
  },
  infoTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: '#4FCCC6',
    margin: '0 0 8px',
  },
  infoList: {
    fontSize: 13,
    lineHeight: 1.8,
    color: 'rgba(255,255,255,0.6)',
    paddingLeft: 18,
    margin: 0,
  },
  links: {
    marginBottom: 16,
  },
  link: {
    color: 'rgba(255,255,255,0.4)',
    textDecoration: 'none',
    fontSize: 13,
  },
  dot: {
    color: 'rgba(255,255,255,0.2)',
    margin: '0 8px',
    fontSize: 13,
  },
  footer: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.2)',
    margin: 0,
  },
};

export default VerifyConsent;
